import { Button } from '@/components/common/button'
import { Note } from '@/components/common/note'
import { TabsContent, TabsTrigger } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { InfoIcon } from '@/components/icons/info'
import { LoadingIcon } from '@/components/icons/loading'
import { LockIcon } from '@/components/icons/lock'
import { TxAmountInput } from '@/components/opportunity/sidePanel/txAmountInput'
import { useTransaction } from '@/hooks/useTransaction'
import { useActionsQuery } from '@/queries/actions.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { Vault } from '@/types/vault'
import { calculateTokenPriceInUsd } from '@/utils/calculateTokenPriceInUsd'
import { cn } from '@/utils/cn'
import { pluralize } from '@/utils/pluralize'
import { getTransactionStepData } from '@/utils/transactions/getTransactionStepData'
import {
  DAY_IN_SECONDS,
  HOUR_IN_SECONDS,
  MINUTE_IN_MILLISECONDS,
  SECOND_IN_MILLISECONDS,
} from '@vaultsfyi/common/constants'
import { formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import { type ComponentProps, useMemo, useState } from 'react'
import type { Address } from 'viem'
import { z } from 'zod'
import { AmountButtons } from '../sidePanel/amountButtons'
import { TxSteps } from '../sidePanel/txSteps'
import { useMaxAmount } from '../sidePanel/useMaxAmount'
import { useTxSubmissionBlocked } from '../txPanel/txSubmissionGuard'
import type { TabConfig } from '../useTabsConfig'

export const UMBRELLA_WITHDRAW_TAB_NAME = 'umbrellaWithdraw'

const CooldownStatus = {
  ACTIVE: 'active',
  READY: 'ready',
  EXPIRED: 'expired',
} as const

const withdrawDataSchema = z
  .object({
    amount: z.string(),
    endOfCooldown: z.coerce.number(),
    withdrawalWindow: z.coerce.number(),
    status: z.nativeEnum(CooldownStatus),
  })
  .optional()

type WithdrawData = z.infer<typeof withdrawDataSchema>

export const UmbrellaWithdrawTabTrigger = ({
  transactionsContext,
  className,
}: ComponentProps<'div'> & {
  transactionsContext: NonNullable<TransactionsContext>
}) => {
  const withdrawData = useMemo(() => {
    return withdrawDataSchema.parse((transactionsContext?.vaultSpecificData as Record<string, any>)['cooldownData'])
  }, [transactionsContext])

  const isOpen = withdrawData?.status === 'active'
  const [check, setCheck] = useState(isOpen)
  return (
    <TabsTrigger value={UMBRELLA_WITHDRAW_TAB_NAME} className={className} onClick={() => setCheck((prev) => !prev)}>
      <div className="flex items-center gap-1">
        Withdraw <LockIcon open={check} size={16} />
      </div>
    </TabsTrigger>
  )
}

interface TabProps {
  account: Address | undefined
  tabConfig: TabConfig
  vault: Vault
  txState: 'idle' | 'loading' | 'success'
  transactionsContext: NonNullable<TransactionsContext>
  setTransactionFinished: ({ txHash }: { txHash: string | undefined; amount: string }) => void
}

export function UmbrellaWithdrawTab({ ...props }: TabProps) {
  const { account, transactionsContext } = props
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const withdrawData = useMemo(() => {
    return withdrawDataSchema.parse((transactionsContext?.vaultSpecificData as Record<string, any>)['cooldownData'])
  }, [transactionsContext])

  return (
    <TabsContent value={UMBRELLA_WITHDRAW_TAB_NAME} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
      {!account ? (
        <>
          <p className="text-xs">Connect your wallet to withdraw</p>
          <UmbrellaWithdrawNote />
          <Button type="button" onClick={() => setOpenWalletModal('connect')} className="w-full">
            Connect wallet
          </Button>
        </>
      ) : BigInt(transactionsContext.lpToken.balanceNative) === 0n ? (
        <>
          <p className="text-xs">Connected wallet has no position in this vault</p>
          <UmbrellaWithdrawNote />
        </>
      ) : (
        <>
          {!withdrawData ? (
            <LockedContent {...props} />
          ) : withdrawData.status === 'active' ? (
            <InitiatedContent withdrawData={withdrawData} {...props} />
          ) : (
            <OpenContent withdrawData={withdrawData} {...props} />
          )}
        </>
      )}
    </TabsContent>
  )
}

const LockedContent = ({ account, vault, txState, tabConfig, setTransactionFinished }: TabProps) => {
  const { form } = tabConfig
  const submissionBlocked = useTxSubmissionBlocked()

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: 'start-redeem-cooldown',
    amount: '0',
    vault,
    ignoreAmount: true,
  })
  const { executeTx } = useTransaction(vault.network.name, actions, '0', setTransactionFinished)

  const onSubmit = form.handleSubmit(async () => {
    await executeTx()
  })

  const isSubmitLoading = isFetching || txState === 'loading'

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="text-sm">
        <p>Unlock withdrawals</p>
        <ul className="list-disc pl-5">
          <li>you will be able to withdraw in 20 days</li>
          <li>the withdrawal window will usually last 48 hours</li>
          <li>your funds are earning while you wait</li>
        </ul>
      </div>
      <Button type="submit" className="gap-2" disabled={isSubmitLoading || submissionBlocked}>
        Initiate cooldown
        {isSubmitLoading && <LoadingIcon size={16} className="animate-spin" />}
      </Button>
    </form>
  )
}

const InitiatedContent = ({ withdrawData }: { withdrawData: NonNullable<WithdrawData> }) => {
  const { days, hours, minutes } = getTimeDifference(withdrawData.endOfCooldown)

  return (
    <div className="flex flex-col items-center gap-1 py-5 text-center">
      <p className="text-xl">Cooldown in progress</p>
      <p className="text-xl">
        <span className="font-bold text-3xl text-accent">{days}</span> days{' '}
        <span className="font-bold text-3xl text-accent">{hours}</span> hours{' '}
        <span className="font-bold text-3xl text-accent">{minutes}</span> minutes
      </p>
    </div>
  )
}

const OpenContent = ({
  account,
  vault,
  txState,
  tabConfig,
  setTransactionFinished,
  transactionsContext,
  withdrawData,
}: TabProps & { withdrawData: NonNullable<WithdrawData> }) => {
  const { asset, network } = vault
  const { getParsedAmount, form } = tabConfig
  const inputAmount = form.watch('amount')
  const parsedAmount = getParsedAmount(inputAmount || '0')
  const [useAllFunds, setUseAllFunds] = useState(false)
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()

  const balanceValue = formatPreciseTokenValue({
    value: withdrawData.amount,
    decimals: asset.decimals,
  })
  const inputUsdValue = formatUSD(calculateTokenPriceInUsd(Number(inputAmount), Number(asset.assetPriceInUsd)) || '0')
  const { days, hours, minutes } = getTimeDifference(withdrawData.endOfCooldown + withdrawData.withdrawalWindow)

  const withdrawActions = getTransactionStepData({ actions: transactionsContext, actionName: 'claim-redeem' })

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: 'redeem',
    amount: parsedAmount,
    vault,
    all: useAllFunds,
    ignoreAmount: false,
  })

  const { executeTx, currentActionIndex } = useTransaction(network.name, actions, inputAmount, setTransactionFinished)

  const error = useMemo(() => {
    if (BigInt(parsedAmount) > BigInt(withdrawData.amount)) {
      return `Not enough ${asset.symbol} to withdraw`
    }
    return undefined
  }, [parsedAmount, withdrawData.amount])

  const onSubmit = form.handleSubmit(async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    if (error) {
      return
    }
    await executeTx()
  })

  const isNonIdle = txState !== 'idle'
  const isSubmitDisabled = !withdrawActions || parsedAmount === '0' || !!error
  const isSubmitLoading = isFetching || txState === 'loading'
  const { handleMaxClick } = useMaxAmount({
    form,
    maxAmount: withdrawData.amount,
    decimals: asset.decimals,
    setUseAllFunds,
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <TxAmountInput
        amountForm={form}
        className={cn(isNonIdle && 'hidden')}
        actionDescription={`Withdraw ${asset.symbol} from ${vault.name}`}
        usdValue={inputUsdValue}
        balance={balanceValue}
        asset={asset}
        hasError={!!error}
        onBalanceClick={handleMaxClick}
        onAmountChange={() => setUseAllFunds(false)}
      />

      {error && <div className="rounded-2xl border border-destructive px-3 py-0.5 text-destructive">{error}</div>}

      <AmountButtons
        form={form}
        maxInputAmount={withdrawData.amount}
        setUseAllFunds={setUseAllFunds}
        className={cn(isNonIdle && 'hidden')}
      />

      <TxSteps
        account={account}
        stepData={withdrawActions}
        activeStep={currentActionIndex}
        loading={isSubmitLoading}
        disabled={isSubmitDisabled}
        label="Withdraw"
        network={network.name}
        success={txState === 'success'}
      />

      <p className="text-sm">
        You have <span className="font-bold text-accent">{days}</span> {pluralize(days, 'day')},{' '}
        <span className="font-bold text-accent">{hours}</span> {pluralize(hours, 'hour')} and{' '}
        <span className="font-bold text-accent">{minutes}</span> {pluralize(minutes, 'minute')} left to withdraw.
      </p>
    </form>
  )
}

const AAVE_DOCS_URL = 'https://aave.com/docs'

export const UmbrellaWithdrawNote = () => {
  return (
    <Note>
      <div className="flex items-center gap-2">
        <InfoIcon />
        <h6 className="font-bold">Cooldown period notice</h6>
      </div>
      <p className="text-xs">
        The 20 days cooldown period is required before unstaking tokens. Assets are withdrawable only after this period,
        within a 2-day unstake window.{' '}
        <Button variant="default" size="default" className="text-xs underline" asChild>
          <a href={AAVE_DOCS_URL} target="_blank" rel="noreferrer">
            Learn more
          </a>
        </Button>
      </p>
    </Note>
  )
}

function getTimeDifference(timestamp: number) {
  const now = new Date().getTime()
  const futureDate = timestamp * SECOND_IN_MILLISECONDS
  const diffInMs = futureDate - now

  if (diffInMs < 0) {
    return { days: 0, hours: 0, minutes: 0 }
  }

  const days = Math.floor(diffInMs / (DAY_IN_SECONDS * SECOND_IN_MILLISECONDS))
  const remainingAfterDays = diffInMs % (DAY_IN_SECONDS * SECOND_IN_MILLISECONDS)
  const hours = Math.floor(remainingAfterDays / (HOUR_IN_SECONDS * SECOND_IN_MILLISECONDS))
  const remainingAfterHours = remainingAfterDays % (HOUR_IN_SECONDS * SECOND_IN_MILLISECONDS)
  const minutes = Math.floor(remainingAfterHours / MINUTE_IN_MILLISECONDS)

  return { days, hours, minutes }
}
